import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Autocomplete,
  Grid,
  Stack,
  IconButton,
  Typography,
  Box,
} from '@mui/material';
import { DeleteOutlined as DeleteOutlineIcon, ImageOutlined as ImageOutlinedIcon } from '@mui/icons-material';
import { uid } from '../../lib/format';
import { COUNTRIES } from '../../lib/countries';
import Button from '../../components/Button';
import FormDisclosure from '../../components/FormDisclosure';

const GENDER_OPTIONS = ['Male', 'Female'];
const AGE_RANGE_OPTIONS = ['13-17', '18-24', '25-34', '35-44', '45-54', '55+'];
const LOCATION_OPTIONS = COUNTRIES;

const PLATFORM_GUIDES = {
  Instagram: 'Instagram app → your profile → Professional dashboard → Audience, to see gender, age, and top locations.',
  TikTok: 'TikTok app → Creator tools → Analytics → Followers tab, to see gender, age, and top territories.',
  Douyin: 'Douyin app → Creator Center (创作者中心) → Data (数据) → Fan Portrait (粉丝画像), to see audience breakdowns.',
  Xiaohongshu: 'Xiaohongshu app → Creator Center (创作者中心) → Data (数据) → Fan Portrait (粉丝画像), to see audience breakdowns.',
};

const PLATFORM_GUIDE_IMAGES = {
  Instagram: '/instagram_audience_insights_guide.png',
  TikTok: '/tiktok_audience_insights_guide.png',
  Douyin: '/douyin_audience_insights_guide.png',
};

function seedItems(saved, defaultCategories) {
  if (saved && saved.length > 0) {
    return saved.map((item) => ({ id: item.id || uid('bd'), category: item.category || '', value: item.value ?? '' }));
  }
  return defaultCategories.map((category) => ({ id: uid('bd'), category, value: '' }));
}

function cleanItems(items) {
  return items.filter((item) => item.category && item.value !== '').map(({ category, value }) => ({ category, value: Number(value) }));
}

function BreakdownList({ items, onChange, options, restrictToOptions }) {
  function updateItem(id, patch) {
    onChange(items.map((item) => (item.id === id ? { ...item, ...patch } : item)));
  }
  function addItem() {
    onChange([...items, { id: uid('bd'), category: '', value: '' }]);
  }
  function removeItem(id) {
    onChange(items.filter((item) => item.id !== id));
  }

  return (
    <Grid size={12}>
      <Stack spacing={2.5}>
        {items.map((item) => (
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} key={item.id} sx={{ alignItems: { xs: 'stretch', sm: 'center' } }}>
            <Autocomplete
              freeSolo={!restrictToOptions}
              size="small"
              fullWidth
              options={options}
              value={restrictToOptions ? item.category || null : item.category}
              onChange={(event, newValue) => updateItem(item.id, { category: newValue || '' })}
              {...(!restrictToOptions && {
                onInputChange: (event, newValue) => updateItem(item.id, { category: newValue }),
              })}
              sx={{ minWidth: { sm: 200 } }}
              renderInput={(params) => <TextField {...params} label="Category" />}
            />
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
              <TextField
                size="small"
                type="number"
                label="Value %"
                value={item.value}
                onChange={(event) => updateItem(item.id, { value: event.target.value })}
                sx={{ width: 120, flexShrink: 0 }}
              />
              <IconButton size="small" onClick={() => removeItem(item.id)} aria-label="Remove">
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Stack>
          </Stack>
        ))}
        <Button variant="secondary" onClick={addItem} sx={{ alignSelf: 'flex-start' }}>
          Add
        </Button>
      </Stack>
    </Grid>
  );
}

function AddMetricsModal({ social, onClose, onSubmit }) {
  const [followers, setFollowers] = useState(social.followers || 0);
  const [genderItems, setGenderItems] = useState(seedItems(social.stats?.genderBreakdown, GENDER_OPTIONS));
  const [ageItems, setAgeItems] = useState(seedItems(social.stats?.ageBreakdown, AGE_RANGE_OPTIONS));
  const [locationItems, setLocationItems] = useState(seedItems(social.stats?.locationBreakdown, ['Singapore', 'Malaysia']));
  const [imageOpen, setImageOpen] = useState(false);

  function submit() {
    onSubmit({
      followers: Number(followers),
      stats: {
        genderBreakdown: cleanItems(genderItems),
        ageBreakdown: cleanItems(ageItems),
        locationBreakdown: cleanItems(locationItems),
      },
    });
  }

  const guide = PLATFORM_GUIDES[social.platform];
  const guideImage = PLATFORM_GUIDE_IMAGES[social.platform];

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{social.platform} audience insights</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          {guide && (
            <Grid size={12}>
              <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
                <Typography variant="body2" color="text.secondary">
                  Not sure where to find this?
                </Typography>
                <Stack
                  direction="row"
                  spacing={1}
                  onClick={() => setImageOpen(true)}
                  sx={{
                    alignItems: 'center',
                    cursor: 'pointer',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 999,
                    pr: 1.5,
                    pl: 0.5,
                    py: 0.5,
                    '&:hover': { borderColor: 'primary.main' },
                  }}
                >
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: '50%',
                      bgcolor: 'grey.200',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <ImageOutlinedIcon sx={{ color: 'grey.400', fontSize: 16 }} />
                  </Box>
                  <Typography variant="body2" fontWeight={600} color="primary.main">
                    See guide
                  </Typography>
                </Stack>
              </Stack>
            </Grid>
          )}
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              type="number"
              label="Follower count"
              value={followers}
              onChange={(event) => setFollowers(event.target.value)}
            />
          </Grid>

          <Grid size={12}>
            <FormDisclosure label="Gender">
              <BreakdownList items={genderItems} onChange={setGenderItems} options={GENDER_OPTIONS} />
            </FormDisclosure>
          </Grid>

          <Grid size={12}>
            <FormDisclosure label="Age range">
              <BreakdownList items={ageItems} onChange={setAgeItems} options={AGE_RANGE_OPTIONS} />
            </FormDisclosure>
          </Grid>

          <Grid size={12}>
            <FormDisclosure label="Location">
              <BreakdownList items={locationItems} onChange={setLocationItems} options={LOCATION_OPTIONS} restrictToOptions />
            </FormDisclosure>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        <Button onClick={submit}>Save audience insights</Button>
      </DialogActions>
      {guide && (
        <Dialog open={imageOpen} onClose={() => setImageOpen(false)} maxWidth="xs">
          {guideImage ? (
            <Box
              component="img"
              src={guideImage}
              alt={`Where to find ${social.platform} audience insights`}
              sx={{ width: '100%', display: 'block' }}
            />
          ) : (
            <Box sx={{ p: 3 }}>
              <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                Where to find your {social.platform} audience insights
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {guide}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2, fontStyle: 'italic' }}>
                *Interface may vary slightly by account type and app version. If you're not sure where to find your audience insights,
                contact our team and we'll help you out.
              </Typography>
            </Box>
          )}
        </Dialog>
      )}
    </Dialog>
  );
}

export default AddMetricsModal;
